import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { CreateRecordProperties, DSSetup, PopupData, Service } from 'webapp-types';
import {
	DateRangeSelectionId,
	GranulationTypeId,
	ModelName,
	ScheduleType,
	ScorecardType,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject, equal, getMostCommonElement } from 'webapp/utils/common';

import ScorecardPreviewPopupComponent from 'webapp/components/scorecard/preview/popup';

import type { MetricSetup } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type PopupModal from 'webapp/models-custom/popup/modal';
import Scorecard from 'webapp/models-custom/scorecard';
import type DataSelectRecord from 'webapp/models/data-select';
import type ScorecardRecord from 'webapp/models/scorecard';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TrackingOriginForCreateOrEdit as DigestsTrackingOriginForCreateOrEdit } from 'webapp/services/model-manager/metric-digests';

export type TrackingOriginForCreateOrEdit = 'notifications' | 'metrics';

const defaultTrackingOriginForCreateOrEdit: TrackingOriginForCreateOrEdit = 'notifications';

export default class ModelManagerScorecardsService extends ModelManagerForStoreService<
	ScorecardRecord,
	Scorecard
> {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('constants') declare constantsService: Service.Constants;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	@cached
	get savedScorecardRecords(): ScorecardRecord[] {
		return this.savedRecords.filter((item) => item.type === ScorecardType.SCORECARD);
	}

	get scorecardModels(): Scorecard[] {
		return this.createModelsFromRecordsArray(this.savedScorecardRecords);
	}

	createModelForMetricSetup(
		metricSetup: MetricSetup,
		origin: TrackingOriginForCreateOrEdit | DigestsTrackingOriginForCreateOrEdit,
		skipTracking = false,
	) {
		const scorecard = this.createScorecard(origin, skipTracking);
		const dataSelect = this.createDataSelectForScorecard(
			scorecard,
			metricSetup.metricSetupData!,
		);
		scorecard.presetDataSelectsOnCreate = [dataSelect];
		return scorecard;
	}

	edit(
		model: Scorecard,
		origin: TrackingOriginForCreateOrEdit | DigestsTrackingOriginForCreateOrEdit,
		isFromCreateChoicePopupFlow = false,
	) {
		this.mmNotificationsService.openEditPopup(model, origin, isFromCreateChoicePopupFlow);
	}

	createAndEdit(origin: TrackingOriginForCreateOrEdit | DigestsTrackingOriginForCreateOrEdit) {
		const model = this.createScorecard(origin);
		this.edit(model, origin);
	}

	createAndEditForMetricSetup(
		metricSetup: MetricSetup,
		origin: TrackingOriginForCreateOrEdit | DigestsTrackingOriginForCreateOrEdit,
		isFromCreateChoicePopupFlow = false,
	) {
		const model = this.createModelForMetricSetup(metricSetup, origin);
		this.edit(model, origin, isFromCreateChoicePopupFlow);
	}

	private createScorecard(origin: TrackingOriginForCreateOrEdit, skipTracking = false) {
		const scorecard = this.createNewModel();
		if (!skipTracking) {
			this.analyticsService.event(TrackingEvent.NEW_SCORECARD, { origin });
		}
		return scorecard;
	}

	createDataSelectForScorecard(
		scorecard: Scorecard,
		metricSetupData?: DSSetup.MetricSetupData,
	): DataSelect {
		const dateRange = scorecard.dateRangeCM;

		return this.mmDataSelectsService.createNewModel({
			metricSetupKey: metricSetupData?.key,
			metricSetupSpaceAccessId: metricSetupData?.spaceAccessId || null,
			metricSetupDimensions: metricSetupData?.dimensions || [],
			metricSetupFilters: metricSetupData?.filters || null,
			skipAutoUpdate: true,
			blockId: scorecard.blockId,
			dateRange: dateRange.getFormula(),
			compares: [this.createPreviousPeriodCompareForDateRange(dateRange).getFormula()!],
		});
	}

	createDuplicatedModel(model: Scorecard) {
		const data = model.getDataForClone(false);
		const copy = this.createNewModel(data);
		copy.presetDataSelectsOnCreate = this.createDuplicatesForModelDataSelects(model);
		return copy;
	}

	protected createDuplicatesForModelDataSelects(model: Scorecard) {
		return model.savedDataSelects.map((ds) => {
			const data = ds.toJSON();
			delete data.blockId;
			return this.mmDataSelectsService.createNewModel(data);
		});
	}

	deleteScorecard(
		scorecard: Scorecard,
		afterDelete?: () => void,
		origin: TrackingOriginForCreateOrEdit = defaultTrackingOriginForCreateOrEdit,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const layer = this.infolayerService.loading('Deleting scorecard...');
			scorecard
				.delete()
				.then(() => {
					layer.ok('Scorecard deleted!');
					this.analyticsService.event(TrackingEvent.SCORECARD_DELETED, {
						origin,
					});
					if (afterDelete) {
						afterDelete();
					}
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete the scorecard "${scorecard.title}"?`,
			text: scorecard.deleteText,
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	openPreviewPopup(props: PopupData.Scorecard.Preview): PopupModal {
		const popup = this.popupService.createModal(ScorecardPreviewPopupComponent, {
			popupClass: 'popup-type-scorecard-preview',
		});
		popup.open(props);

		return popup;
	}

	openPreviewPopupFromModel(model: Scorecard) {
		const { id, title, color, dateIntervalText, savedDataSelects } = model;

		next(() => {
			// running in runloop to ensure that right dateIntervalText is shown. Task: https://app.asana.com/0/40713484505492/1199950710879028/f
			this.openPreviewPopup({
				id: id!,
				title,
				color,
				dateIntervalText,
				items: savedDataSelects,
			});
		});
	}

	toggleIsActive(model: Scorecard) {
		model.isActive = !model.isActive;
		void model.saveAndCatch();
	}

	getCommonCompareFromDsRecordArray(dsRecords: DataSelectRecord[]) {
		const compares = dsRecords.map((ds: DataSelectRecord) => {
			const dateRange = DateRange.createFromFormula(ds.dateRange);
			return DsCompare.createFromFormula(ds.compares[0] || null, dateRange);
		});

		const commonRangeTypeId = getMostCommonElement(compares.map((item) => item.typeId));
		return compares.find((item) => item.typeId === commonRangeTypeId);
	}

	createPreviousPeriodCompareForDateRange(dateRange: DateRange) {
		const compare = DsCompare.createFromFormula(
			{
				type: 1,
				dateRange: {
					offset: {
						timeUnit: dateRange.rangeTimeUnit || 0,
						offset: -1,
					},
				},
			},
			dateRange,
		);

		const predefinedCompare = DsCompare.predefinedCompares.find(
			(item) => item.selectionId === DsCompare.customPreviousOffsetId,
		);

		compare.title = 'Previous period';
		compare.categoryId = predefinedCompare?.categoryId || 0;
		compare.typeIdVariations = predefinedCompare?.typeIdVariations || [];

		return compare;
	}

	updateDataSelectFromAnother(targetDataSelect: DataSelect, sourceDataSelect: DataSelect) {
		// this method is used for scorecard item (updating from advanced/resetting changes)
		// extending updateDataSelectFromAnother from scripts service (for metric, attributes, units, dateRange)
		// in addition to that, also update format, scale and the compare
		this.mmScriptsService.updateDataSelectFromAnother(targetDataSelect, sourceDataSelect);

		if (!equal(targetDataSelect.overrides, sourceDataSelect.overrides)) {
			targetDataSelect.overrides = copyObject(sourceDataSelect.overrides);
		}

		if (!equal(targetDataSelect.compares, sourceDataSelect.compares)) {
			targetDataSelect.compares = copyObject(sourceDataSelect.compares);
		}
	}

	// ------ Requests ------

	_scorecardsRequested = false;
	_scorecardsRequestPromise: Promise<void> | null = null;
	requestScorecards(): Promise<void> {
		const variablePrefix = 'scorecards';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				// const fakeData = window.dbx_mock['GET /d/scorecards'];
				// this.storeService.pushPayload(fakeData);
				// return Promise.resolve({});

				return this.requestService.storeFindAll(ModelName.SCORECARD);
			},
		);
	}

	// ------ For-store ------

	modelName = ModelName.SCORECARD;

	createModel(model: ScorecardRecord): Scorecard {
		return new Scorecard({ model });
	}

	override createNewModel(properties: CreateRecordProperties<ScorecardRecord> = {}): Scorecard {
		const defaultTitle = 'Scorecard';

		if (typeof properties.type === 'undefined') {
			properties.type = ScorecardType.SCORECARD;
		}
		if (!properties.title) {
			properties.title = defaultTitle;
		}
		if (!properties.color) {
			properties.color = this.mmBoardsService.getRandomColor();
		}
		if (typeof properties.scheduleType === 'undefined') {
			properties.scheduleType = ScheduleType.DAILY;
		}
		if (typeof properties.dateRange === 'undefined') {
			const dateRange = DateRange.createFromRangeUISelectionIdWithGranulationId(
				DateRangeSelectionId.MONTH_TO_DATE,
				GranulationTypeId.TOTAL,
			);
			properties.dateRange = dateRange.formula;
		}

		return super.createNewModel(properties);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/scorecards': ModelManagerScorecardsService;
	}
}
